import { DEFAULT_WORKTREE_SETTINGS, type WorktreeSettings } from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { Button } from '@crowdex/ui/components/button';
import { Input } from '@crowdex/ui/components/input';
import { Minus, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  SettingLinkRow,
  SettingRow,
  SettingsGroup,
  SettingsNote,
  SettingToggle,
} from '@/components/settings-page/setting-controls';
import { isElectronDesktop, openDirectoryDialog } from '@/lib/electron-bridge';
import {
  type LocalConversation,
  type LocalWorkspace,
  readWorktreeSettings,
  saveWorktreeSettings,
} from '@/lib/local-conversations';
import { useRouter } from '@/lib/navigation';
import { type WorktreeInUse, worktreesInUse } from '@/lib/settings-page/worktree-preferences';

const DEFAULT_ROOT = '~/.crowdex/worktrees';
/** How long a typed folder waits for the typing to pause before it is kept. */
const TYPING_PAUSE_MS = 400;

/** This machine's managed worktrees with the conversations working in them. */
function useWorktreesInUse(local: boolean): WorktreeInUse[] | null {
  const [worktrees, setWorktrees] = useState<WorktreeInUse[] | null>(null);
  useEffect(() => {
    if (!local) return;
    let live = true;
    Promise.all([
      apiFetch<LocalWorkspace[]>('/api/local/workspaces'),
      apiFetch<LocalConversation[]>('/api/local/conversations'),
    ]).then(
      ([workspaces, conversations]) =>
        live && setWorktrees(worktreesInUse(workspaces, conversations)),
      () => live && setWorktrees([]),
    );
    return () => {
      live = false;
    };
  }, [local]);
  return worktrees;
}

/** Settings › Worktrees (D42, phone 39): where task worktrees live and how they are cleaned up. */
/** A worktree's row title: its name, then the conversation working in it. */
function worktreeTitle(worktree: WorktreeInUse) {
  const conversation = worktree.conversations[0];
  return (
    <span>
      <span className="font-mono">{worktree.name}</span>
      {conversation && ` · ${conversation.title}`}
    </span>
  );
}

export function WorktreeSettingsSection({
  phone = false,
  local = false,
}: {
  phone?: boolean;
  /** Whether this server runs on the user's computer, where its worktrees are. */
  local?: boolean;
}) {
  const router = useRouter();
  const [settings, setSettings] = useState<WorktreeSettings>(DEFAULT_WORKTREE_SETTINGS);
  const [root, setRoot] = useState('');
  const [error, setError] = useState<string | null>(null);
  const pending = useRef<number | null>(null);
  const latest = useRef<WorktreeSettings>(DEFAULT_WORKTREE_SETTINGS);
  /** Changed here before the server answered: the change is the newer one. */
  const edited = useRef(false);
  const inUse = useWorktreesInUse(local);
  const { autoCleanup, keepCount } = settings;
  useEffect(() => {
    let live = true;
    void readWorktreeSettings().then(
      (loaded) => {
        // A change made meanwhile is the newer one.
        if (!live || edited.current) return;
        latest.current = loaded;
        setSettings(loaded);
        setRoot(loaded.root ?? '');
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);
  // The computer's own setting: every window and device of its server uses it.
  const save = (next: Partial<WorktreeSettings>) => {
    edited.current = true;
    const updated = { ...latest.current, ...next };
    latest.current = updated;
    setSettings(updated);
    setError(null);
    void saveWorktreeSettings(updated).catch(() =>
      setError('Could not save the settings of this computer.'),
    );
  };
  const saveRoot = (value: string, wait: boolean) => {
    setRoot(value);
    edited.current = true;
    if (pending.current !== null) window.clearTimeout(pending.current);
    pending.current = null;
    if (!wait) save({ root: value.trim() || null });
    else
      pending.current = window.setTimeout(() => {
        pending.current = null;
        save({ root: value.trim() || null });
      }, TYPING_PAUSE_MS);
  };
  const keep = (count: number) => save({ keepCount: Math.max(1, Math.min(100, count)) });
  return (
    <>
      <SettingsGroup title="Managed worktrees">
        {!phone && (
          <SettingRow
            description={root.trim() === '' ? `Default: ${DEFAULT_ROOT}` : 'On this computer'}
            title="Folder on disk"
          >
            <Input
              aria-label="Folder on disk"
              className="h-control-sm w-60 font-mono text-xs"
              onBlur={() => saveRoot(root, false)}
              onChange={(event) => saveRoot(event.target.value, true)}
              placeholder={DEFAULT_ROOT}
              value={root}
            />
            {isElectronDesktop() && (
              <Button
                onClick={() =>
                  void openDirectoryDialog().then((chosen) => {
                    if (chosen !== null) saveRoot(chosen, false);
                  })
                }
                size="sm"
                variant="outline"
              >
                Change…
              </Button>
            )}
            {root.trim() !== '' && (
              <Button onClick={() => saveRoot('', false)} size="sm" variant="ghost">
                Use default
              </Button>
            )}
          </SettingRow>
        )}
        <SettingRow description="Remove worktrees of merged branches" title="Automatic cleanup">
          <SettingToggle
            checked={autoCleanup}
            label="Automatic cleanup"
            onChange={(value) => save({ autoCleanup: value })}
          />
        </SettingRow>
        <SettingRow title="Worktrees to keep">
          <span className="flex items-center gap-1 rounded-lg bg-control p-0.5 max-md:p-1">
            <Button
              aria-label="Keep fewer worktrees"
              disabled={!autoCleanup || keepCount <= 1}
              onClick={() => keep(keepCount - 1)}
              size="icon-xs"
              variant="ghost"
            >
              <Minus />
            </Button>
            <output aria-label="Worktrees to keep" className="w-7 text-center text-sm tabular-nums">
              {keepCount}
            </output>
            <Button
              aria-label="Keep more worktrees"
              disabled={!autoCleanup}
              onClick={() => keep(keepCount + 1)}
              size="icon-xs"
              variant="ghost"
            >
              <Plus />
            </Button>
          </span>
        </SettingRow>
      </SettingsGroup>
      {error && <p className="mb-4 text-destructive text-sm">{error}</p>}
      {local && inUse !== null && (
        <SettingsGroup title={`In use · ${inUse.length}`}>
          {inUse.map((worktree) => {
            const conversation = worktree.conversations[0];
            const title = worktreeTitle(worktree);
            return conversation ? (
              <SettingLinkRow
                description={
                  worktree.conversations.length > 1
                    ? `${worktree.conversations.length} conversations`
                    : undefined
                }
                key={worktree.path}
                onSelect={() => router.push(`/desktop?threadId=${conversation.id}`)}
                title={title}
              />
            ) : (
              <SettingRow
                description="No conversation works in it any more"
                key={worktree.path}
                title={title}
              />
            );
          })}
          {inUse.length === 0 && (
            <p className="px-4 py-3.5 text-muted-foreground text-sm">No worktree in use.</p>
          )}
        </SettingsGroup>
      )}
      <SettingsNote className="mt-0">
        {phone
          ? 'The worktree folder on disk is chosen on a computer.'
          : 'Pinned and running conversations are never removed. A worktree of a repository stays until you remove it.'}
      </SettingsNote>
    </>
  );
}
